import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useHtmlOverflowHidden } from "@/hooks/use-html-overflow-hidden";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  useCreateArticleFromFile,
  useUpdateArticleMeta,
  useSyncArticleContent,
  authorFileDetailQueryOptions,
  articleDetailQueryOptions,
} from "@/hooks/use-articles";
import {
  premiumArticlePublishSchema,
  premiumArticleEditSchema,
  type PremiumArticlePublishFormValues,
  type PremiumArticleEditFormValues,
} from "@/lib/validation/article-schema";
import type { AdminAuthorFileDetail, PremiumArticle } from "@/types/article";
import { isPremiumArticle } from "@/types/article";
import { EditorPanel } from "./editor-panel";
import { PreviewPanel } from "./preview-panel";
import { PanelResizeHandle } from "./panel-resize-handle";

const SPLIT_VIEW_BREAKPOINT = 1024;

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${SPLIT_VIEW_BREAKPOINT - 1}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mql.addEventListener("change", onChange);
    setIsNarrow(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isNarrow;
}

type CreateMode = { kind: "create"; file: AdminAuthorFileDetail };
type EditMode = { kind: "edit"; article: PremiumArticle };
export type SplitViewMode = CreateMode | EditMode;

interface PublishSplitViewProps {
  mode: SplitViewMode;
}

export function PublishSplitView({ mode }: PublishSplitViewProps) {
  useHtmlOverflowHidden();
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const { mutate: createFromFile, isPending: isCreatePending } = useCreateArticleFromFile();
  const { mutate: updateArticleMeta, isPending: isUpdatePending } = useUpdateArticleMeta();
  const { mutate: syncContent, isPending: isSyncPending } = useSyncArticleContent();
  const [splitRatio, setSplitRatio] = useState(0.45);
  const [mobileTab, setMobileTab] = useState<"editor" | "preview">("editor");

  const isCreate = mode.kind === "create";

  // Article detail query (edit mode only) - subscribes to cache for live updates after sync
  const articleQuery = useQuery({
    ...articleDetailQueryOptions(mode.kind === "edit" ? mode.article._id : ""),
    enabled: mode.kind === "edit",
  });
  const liveArticle =
    mode.kind === "edit" && articleQuery.data && isPremiumArticle(articleQuery.data)
      ? articleQuery.data
      : mode.kind === "edit"
        ? mode.article
        : null;

  // Source file query (edit mode only) - detects if file has been updated
  const sourceFileQuery = useQuery({
    ...authorFileDetailQueryOptions(
      mode.kind === "edit" ? (mode.article.sourceFileId ?? "") : ""
    ),
    enabled: mode.kind === "edit" && !!mode.article.sourceFileId,
  });

  const hasFileUpdate =
    liveArticle && sourceFileQuery.data
      ? new Date(liveArticle.sourceFileUpdatedAt ?? 0) < new Date(sourceFileQuery.data.updatedAt)
      : false;

  const contentHtml = isCreate ? mode.file.content : (liveArticle?.content.ko ?? mode.article.content.ko);
  const initialFreeHtml = isCreate ? undefined : mode.article.freeContent.ko;

  const [freeContentHtml, setFreeContentHtml] = useState(initialFreeHtml ?? "");

  const schema = isCreate ? premiumArticlePublishSchema : premiumArticleEditSchema;
  const defaultValues = isCreate
    ? {
        fileId: mode.file._id,
        title: { ko: mode.file.title, en: null },
        excerpt: { ko: "", en: null },
        thumbnail: {
          url: { ko: "", en: null },
          alt: { ko: "", en: null },
        },
        freeContentHtml: "",
        authorInfo: {
          name: { ko: "", en: null },
        },
        publishNow: false,
      }
    : {
        excerpt: mode.article.excerpt,
        thumbnail: mode.article.thumbnail ?? {
          url: { ko: "", en: null },
          alt: { ko: "", en: null },
        },
        freeContentHtml: mode.article.freeContent.ko,
        authorInfo: mode.article.authorInfo,
      };

  const methods = useForm<PremiumArticlePublishFormValues | PremiumArticleEditFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleFreeContentChange = useCallback(
    (html: string) => {
      setFreeContentHtml(html);
      methods.setValue("freeContentHtml", html, { shouldValidate: true });
    },
    [methods]
  );

  const isPending = isCreate ? isCreatePending : isUpdatePending;

  const onSubmit = (data: PremiumArticlePublishFormValues | PremiumArticleEditFormValues) => {
    if (mode.kind === "create") {
      const createData = data as PremiumArticlePublishFormValues;
      createFromFile({
        fileId: createData.fileId,
        excerpt: createData.excerpt,
        thumbnail: createData.thumbnail,
        authorInfo: createData.authorInfo,
        freeContentHtml: createData.freeContentHtml,
        publishNow: createData.publishNow,
      });
    } else {
      const editData = data as PremiumArticleEditFormValues;
      updateArticleMeta(
        {
          articleId: mode.article._id,
          excerpt: editData.excerpt,
          thumbnail: editData.thumbnail,
          authorInfo: editData.authorInfo,
          freeContentHtml: editData.freeContentHtml,
        },
        {
          onSuccess: () => navigate("/articles"),
        }
      );
    }
  };

  const cancelPath = isCreate ? "/articles/files" : "/articles";
  const submitLabel = isCreate
    ? isPending
      ? "생성 중..."
      : "프리미엄 아티클 생성"
    : isPending
    ? "저장 중..."
    : "수정 저장";

  const containerRef = useRef<HTMLDivElement>(null);
  const savedRatioRef = useRef<number>(0.45);

  const handleResize = useCallback((ratio: number | ((prev: number) => number)) => {
    setSplitRatio((prev) => (typeof ratio === "function" ? ratio(prev) : ratio));
  }, []);

  const handleTogglePreviewWidth = useCallback(
    (enabled: boolean) => {
      if (!containerRef.current) return;

      if (enabled) {
        savedRatioRef.current = splitRatio;
        const containerWidth = containerRef.current.offsetWidth;
        const CONTENT_WIDTH = 738;
        const PADDING_ESTIMATE = 112;
        const requiredWidth = CONTENT_WIDTH + PADDING_ESTIMATE;
        const newRatio = Math.min(0.75, requiredWidth / containerWidth);
        setSplitRatio(newRatio);
      } else {
        setSplitRatio(savedRatioRef.current);
      }
    },
    [splitRatio]
  );

  const syncInfo =
    mode.kind === "edit" && mode.article.sourceFileId
      ? {
          title: sourceFileQuery.data?.title ?? (liveArticle?.title.ko ?? mode.article.title.ko),
          hasUpdate: hasFileUpdate,
          lastSyncedAt: liveArticle?.sourceFileUpdatedAt ?? mode.article.sourceFileUpdatedAt,
          onSync: () => syncContent(mode.article._id),
          isSyncing: isSyncPending,
        }
      : undefined;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="-m-4 flex h-[calc(100vh-var(--header-height,64px))] flex-col md:-m-6"
      >
        {isNarrow ? (
          /* Mobile: Tab-based layout */
          <Tabs
            value={mobileTab}
            onValueChange={(v) => setMobileTab(v as "editor" | "preview")}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <TabsList className="mx-4 mt-2 grid w-auto grid-cols-2">
              <TabsTrigger value="editor">편집</TabsTrigger>
              <TabsTrigger value="preview">미리보기</TabsTrigger>
            </TabsList>

            <TabsContent value="editor" className="mt-0 flex-1 overflow-hidden">
              <EditorPanel
                contentHtml={contentHtml}
                onFreeContentChange={handleFreeContentChange}
                initialFreeHtml={initialFreeHtml}
                syncInfo={syncInfo}
              />
            </TabsContent>

            <TabsContent value="preview" className="mt-0 flex-1 overflow-hidden">
              <PreviewPanel fullContentHtml={contentHtml} freeContentHtml={freeContentHtml} />
            </TabsContent>
          </Tabs>
        ) : (
          /* Desktop: Side-by-side split view */
          <div ref={containerRef} className="flex flex-1 overflow-hidden">
            <div className="overflow-hidden" style={{ flexBasis: `${splitRatio * 100}%`, minWidth: "360px" }}>
              <EditorPanel
                contentHtml={contentHtml}
                onFreeContentChange={handleFreeContentChange}
                onTogglePreviewWidth={handleTogglePreviewWidth}
                initialFreeHtml={initialFreeHtml}
                syncInfo={syncInfo}
              />
            </div>

            <PanelResizeHandle onResize={handleResize} />

            <div className="overflow-hidden" style={{ flexBasis: `${(1 - splitRatio) * 100}%`, minWidth: "360px" }}>
              <PreviewPanel fullContentHtml={contentHtml} freeContentHtml={freeContentHtml} />
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="bg-background flex items-center justify-end gap-3 border-t px-4 py-3">
          <Button type="button" variant="outline" onClick={() => navigate(cancelPath)} disabled={isPending}>
            취소
          </Button>
          <Button type="submit" disabled={isPending}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
